import { FEATURE_FLAGS } from "@solomon-apps/config";
import {
    ErrorBoundary,
    PageErrorFallback,
    useOrgAuth,
    useOrgAuthStore,
    WalletProvider,
    YaasShell
} from "@solomon-apps/shared";
import { YaasTopBarClassic } from "@solomon-apps/shared/src/components/Yaas/YaasTopBarClassic";
import React from "react";
import { Outlet, useLocation } from "react-router-dom";

import { AuthGuard } from "@/components/AuthGuard";
import { ClientOperatingContextSelector } from "@/components/ClientOperatingContextSelector";
import { OnboardingGate } from "@/components/OnboardingGate";
import {
    buildClientBottomNavItems,
    buildClientNavItems,
    CLIENT_HOME_PATH
} from "@/constants/navigation";
import { CLIENT_OAUTH_CONFIG } from "@/constants/oauthConfig";
import { useClientComplianceRequirements } from "@/hooks/useClientComplianceRequirements";
import { useClientPlatformAccess } from "@/hooks/useClientPlatformAccess";

/**
 * Routes that render full-bleed — without the YaaS shell's left sidebar
 * and top bar. Used for self-contained flows where the chrome would be
 * a distraction (compliance onboarding hub).
 */
const FULL_PAGE_ROUTES: readonly string[] = ["/onboarding"];

function isFullPage(pathname: string): boolean {
    return FULL_PAGE_ROUTES.some(
        (path) => pathname === path || pathname.startsWith(`${path}/`)
    );
}

/**
 * Tai's (May-8) top bar is the console default across all shell pages. A
 * few routes opt back into the current/other-dev header — e.g. the parked
 * `/policies-jayesh` reference page. The shared sidebar is unchanged; only
 * the header swaps via `renderTopBar`.
 */
const DEFAULT_HEADER_ROUTES: readonly string[] = ["/policies-jayesh"];

function usesDefaultHeader(pathname: string): boolean {
    return DEFAULT_HEADER_ROUTES.some(
        (path) => pathname === path || pathname.startsWith(`${path}/`)
    );
}

export function Layout(): React.ReactElement {
    const me = useOrgAuthStore((s) => s.me);
    const { logout } = useOrgAuth(CLIENT_OAUTH_CONFIG);
    const { pathname } = useLocation();
    const { consoleAvailable, distributionsAccess, apiAccess } =
        useClientPlatformAccess();
    const { requirements: complianceRequirements } =
        useClientComplianceRequirements({
            enabled: FEATURE_FLAGS.clientComplianceSurface && consoleAvailable
        });
    const navItems = buildClientNavItems({
        distributionsAccess,
        complianceRequirementCount:
            FEATURE_FLAGS.complianceModuleUi &&
            FEATURE_FLAGS.clientComplianceSurface &&
            complianceRequirements
                ? Math.max(
                      complianceRequirements.summary.requirementCount,
                      complianceRequirements.requirements.length
                  )
                : 0
    });

    const user = me
        ? {
              fullName: me.user.fullName ?? me.user.email,
              email: me.user.email,
              orgName: me.org.name,
              orgRole: me.orgRole,
              issuerName: me.issuerOrg?.name ?? null
          }
        : undefined;

    const fullPage = isFullPage(pathname);
    const defaultHeader = usesDefaultHeader(pathname);

    return (
        <AuthGuard>
            <OnboardingGate>
                <WalletProvider>
                    {fullPage ? (
                        <ErrorBoundary fallback={PageErrorFallback}>
                            <Outlet />
                        </ErrorBoundary>
                    ) : (
                        <YaasShell
                            navItems={navItems}
                            bottomNavItems={buildClientBottomNavItems(
                                me?.orgRole === "Owner",
                                { apiAccess }
                            )}
                            sidebarAccessory={
                                <ClientOperatingContextSelector />
                            }
                            consoleKind="client"
                            appLabel="Company Recipient Console"
                            expandedSidebarWidthClass="w-52"
                            // Force the rail to icons once the Programs detail
                            // panel would drop below its @xl grid→stack width:
                            // 576 (panel) + 208 (w-52 rail) + 320 (w-80 list) = 1104.
                            autoCollapseSidebarBelow={1104}
                            user={user}
                            onLogout={() => void logout()}
                            drawerSide="bottom"
                            renderTopBar={
                                defaultHeader
                                    ? undefined
                                    : ({
                                          collapsed,
                                          onMenuClick,
                                          menuOpen
                                      }): React.ReactElement => (
                                          <YaasTopBarClassic
                                              appLabel="Company Recipient Console"
                                              user={user}
                                              sidebarCollapsed={collapsed}
                                              onMenuClick={onMenuClick}
                                              menuOpen={menuOpen}
                                              logoTo={CLIENT_HOME_PATH}
                                          />
                                      )
                            }
                        >
                            <ErrorBoundary fallback={PageErrorFallback}>
                                <Outlet />
                            </ErrorBoundary>
                        </YaasShell>
                    )}
                </WalletProvider>
            </OnboardingGate>
        </AuthGuard>
    );
}
